import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { IconLoading1, IconPurchaseAuthBadge } from 'components/Icons'
import { getConfig } from 'core/singleton'

export interface CardBottomOverlayProps extends Partial<ProductListDetail> {
  clickable?: boolean
  isShowPurchaseAuthBadge?: boolean
  small?: boolean
  onClickProduct?: (e: React.MouseEvent<HTMLDivElement>) => void
}

const CardBottomOverlay: React.FC<CardBottomOverlayProps> = ({
  clickable = true,
  currencyNotation,
  discountRate,
  discountTypeCode,
  isShowPurchaseAuthBadge,
  price,
  priceTypeCode,
  productName,
  productThumbnailUrlList,
  sellingPrice,
  small,
  onClickProduct,
}) => {
  const { isProductPriceVisible } = getConfig()

  const getPriceText = useCallback(
    (_price: number | undefined) => {
      const options = {
        maximumFractionDigits: 2,
      }
      const price = Number(_price).toLocaleString('ko-KR', options) ?? 0

      if (!currencyNotation || currencyNotation === '₩') {
        return `${price}원`
      }

      return `${currencyNotation + price}`
    },
    [currencyNotation],
  )

  const src = useMemo(
    () => productThumbnailUrlList && productThumbnailUrlList[0].thumbnailUrl + '?w=96&h=96&f=jpg',
    [productThumbnailUrlList],
  )

  const productEl = useMemo(() => {
    return !small && src ? (
      <div className="bottom_overlay_product" onClick={onClickProduct}>
        <div className="bottom_overlay_image" style={{ backgroundImage: `url(${src})` }} />
        <div className="bottom_overlay_info">
          <div className="product_name">{productName}</div>
          {isProductPriceVisible ? (
            <>
              {priceTypeCode === 'basic' && (
                <div className="product_price">
                  {discountTypeCode === 'none' ? (
                    <div className="price">{getPriceText(price)}</div>
                  ) : (
                    <>
                      <div className="discount">{discountRate}%</div>
                      <div className="price">{getPriceText(sellingPrice)}</div>
                    </>
                  )}
                </div>
              )}
              {priceTypeCode === 'text' && <div className="product_price_text">{price}</div>}
            </>
          ) : null}
        </div>
        {!clickable && (
          <LoadingBox>
            <Dimmed />
            <IconLoading1 />
          </LoadingBox>
        )}
      </div>
    ) : null
  }, [
    clickable,
    discountRate,
    discountTypeCode,
    isProductPriceVisible,
    price,
    priceTypeCode,
    productName,
    sellingPrice,
    small,
    src,
    getPriceText,
    onClickProduct,
  ])

  const badgeEl = useMemo(() => {
    return isShowPurchaseAuthBadge ? (
      <ul className="bottom_overlay_badge">
        <li className="badge_purchase_auth">
          <IconPurchaseAuthBadge />
        </li>
      </ul>
    ) : null
  }, [isShowPurchaseAuthBadge])

  return (
    <StyledBottom isProductPriceVisible={!!isProductPriceVisible} productClickable={clickable}>
      {badgeEl}
      {productEl}
    </StyledBottom>
  )
}

export default CardBottomOverlay

const StyledBottom = styled.div<{ isProductPriceVisible: boolean; productClickable: boolean }>`
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  z-index: 30;

  .bottom_overlay_badge {
    height: 34px;
    padding-left: 0;
    margin: 0;
    list-style-type: none;

    > li {
      margin-right: 8px;
    }

    .badge_purchase_auth {
      width: 34px;
      height: 34px;
    }
  }

  .bottom_overlay_product {
    margin-top: 8px;
    display: flex;
    border-radius: 4px;
    overflow: hidden;
    backdrop-filter: blur(2px);
    background-color: rgba(0, 0, 0, 0.6);
    height: 48px;
    cursor: ${({ productClickable }) => (productClickable ? 'pointer' : 'default')};

    .bottom_overlay_image {
      flex: 0 0 48px;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
    }

    .bottom_overlay_info {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: ${(props) => (props.isProductPriceVisible ? 'initial' : 'center')};
      padding: 5px 8px;
      overflow: hidden;

      .product_name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        line-height: 20px;
        color: #ffffff;
      }
      .product_price {
        display: flex;
        color: #ffffff;
        font-weight: bold;
        font-size: 14px;
        line-height: 18px;

        .discount {
          margin-right: 4px;
          color: ${(props) => props.theme.discount};
        }
        .price {
          color: inherit;
          font-size: inherit;
          font-weight: inherit;
        }
      }

      .product_price_text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #ffffff;
        font-weight: bold;
        font-size: 14px;
        line-height: 18px;
      }
    }
  }
`
const LoadingBox = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;

  & > svg {
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
  }
`
const Dimmed = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: inherit;
  height: inherit;
  background-color: rgba(0, 0, 0, 0.5);
`
